
import { debugLogger } from '@/utils/debugLogger';

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useDataCentral } from "@/hooks/useDataCentral";
import { useConnectionActions } from "@/hooks/connections/useConnectionActions";
import { useOptimizedPresence } from "@/hooks/useOptimizedPresence";
import { useConnectionsRealtime } from "@/hooks/useConnectionsRealtime";
import { useNotifications } from "@/contexts/NotificationsContext";
import { useProfileViews } from "@/hooks/useProfileViews";
import { useProfileViewsAccess } from "@/hooks/useProfileViewsAccess";

// Components
import ConnectionsHeader from "@/components/connections/ConnectionsHeader";
import ConnectionNavigationBar from "@/components/connections/ConnectionNavigationBar";
import ConnectionTabs from "@/components/connections/ConnectionTabs";

const Connections = () => {
  const [activeTab, setActiveTab] = useState<"sent" | "received" | "matched" | "views">("received");
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use optimized presence system that respects cache rules
  const { updatePresenceStatus, markUserActivity } = useOptimizedPresence({
    conversationId: null,
    userId: user?.id || null,
    partnerId: null
  });

  // Use centralized data system for connections
  const { 
    sentConnections,
    receivedConnections, 
    matchedConnections,
    loading, 
    error, 
    refreshData
  } = useDataCentral({ 
    dataType: 'connections'
  });

  // Hook for connection actions
  const { likeProfile, unlikeProfile, handleUnmatch, deleteConnectionById, likeLimits } = useConnectionActions();

  // Hook for profile views
  const { views } = useProfileViews();

  // Hook for profile views access (blur overlay only for views tab)
  const { shouldShowBlur: shouldShowViewsBlur, loading: viewsAccessLoading } = useProfileViewsAccess();

  // Set up real-time subscriptions for connections
  useConnectionsRealtime(refreshData);

  // Global notifications integration
  const { } = useNotifications();

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!user && !loading) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // Fallback para detectar perda de sessão após inatividade
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!user && !loading && !window.location.pathname.includes('/auth')) {
        debugLogger.warn('Connections', 'Session lost after inactivity, redirecting to auth');
        navigate('/auth', { replace: true });
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [user, loading, navigate]);

  // Update presence when page loads - using optimized system
  useEffect(() => {
    if (user) {
      // Set user as online first
      updatePresenceStatus('online');
      // Then mark specific activity
      markUserActivity('connections_browsing');
    }
  }, [user, updatePresenceStatus, markUserActivity]);

  const getCurrentConnections = () => {
    switch (activeTab) {
      case "sent":
        return sentConnections || [];
      case "received":
        return receivedConnections || [];
      case "matched":
        return matchedConnections || [];
      default:
        return [];
    }
  };

  const currentConnections = getCurrentConnections();

  const handleRetry = () => {
    refreshData();
  };

  // Helper function to convert activeTab to the format expected by ConnectionTabs
  const getTabForConnectionTabs = (tab: "sent" | "received" | "matched" | "views"): "sent" | "received" | "matches" | "views" => {
    return tab === "matched" ? "matches" : tab;
  };

  const handleTabChange = (tab: "sent" | "received" | "matched" | "views") => {
    setActiveTab(tab);
  };

  const handleProfileAction = async (id: string | number, actionType: 'like' | 'unlike' | 'message') => {
    debugLogger.debug('Connections', 'Profile action triggered', { id, actionType, activeTab });
    
    if (actionType === 'like') {
      // Handle "like back" action for received connections
      debugLogger.debug('Connections', 'Processing like action (accept connection)');
      const success = await likeProfile(String(id));
      
      if (success) {
        debugLogger.debug('Connections', 'Like action successful, refreshing data');
        refreshData();
      } else {
        debugLogger.error('Connections', 'Like action failed');
      }
    } else if (actionType === 'unlike') {
      let success = false;
      
      if (activeTab === 'matched') {
        // For matches, convert to sent like instead of deleting
        debugLogger.debug('Connections', 'Handling unmatch for matched connection');
        success = await handleUnmatch(String(id));
      } else if (activeTab === 'received') {
        // For received connections, delete by connection ID
        debugLogger.debug('Connections', 'Handling unlike for received connection - using connection ID');
        
        // Find the connection to get its ID
        const connection = currentConnections.find(conn => conn.id === String(id));
        if (connection && connection.connectionId) {
          debugLogger.debug('Connections', 'Found connection ID for deletion', {
            profileId: id, 
            connectionId: connection.connectionId 
          });
          success = await deleteConnectionById(connection.connectionId);
        } else {
          debugLogger.error('Connections', 'Connection ID not found for received connection', { 
            profileId: id, 
            connection 
          });
        }
      } else {
        // For sent connections, use regular unlike
        debugLogger.debug('Connections', 'Handling unlike for sent connection');
        success = await unlikeProfile(String(id));
      }
      
      if (success) {
        debugLogger.debug('Connections', 'Unlike operation successful, refreshing data');
        refreshData();
      } else {
        debugLogger.error('Connections', 'Unlike operation failed');
      }
    } else if (actionType === 'message') {
      // Navigate to chat with this user
      const connection = currentConnections.find(conn => conn.id === id);
      if (connection) {
        debugLogger.debug('Connections', 'Navigating to chat', { username: connection.username });
        navigate(`/main/chat/${connection.username || connection.id}`);
      }
    }
  };

  if (loading || viewsAccessLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
        <ConnectionsHeader 
          activeTab={activeTab}
          sentCount={0}
          receivedCount={0}
          matchedCount={0}
          viewsCount={0}
        />
        
        <ConnectionNavigationBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          counts={{
            sent: 0,
            received: 0,
            matched: 0,
            views: 0
          }}
        />

        <div className="max-w-4xl mx-auto p-4">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
        <ConnectionsHeader 
          activeTab={activeTab}
          sentCount={0}
          receivedCount={0}
          matchedCount={0}
          viewsCount={0}
        />
        
        <ConnectionNavigationBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          counts={{
            sent: 0,
            received: 0,
            matched: 0,
            views: 0
          }}
        />

        <div className="max-w-4xl mx-auto p-4">
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">{error}</p>
            <button 
              onClick={handleRetry}
              className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-black via-black to-gray-900 flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <ConnectionsHeader 
        activeTab={activeTab}
        sentCount={sentConnections?.length || 0}
        receivedCount={receivedConnections?.length || 0}
        matchedCount={matchedConnections?.length || 0}
        viewsCount={views?.length || 0}
      />
      
      {/* New Navigation Bar - integrated into layout */}
      <ConnectionNavigationBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={{
          sent: sentConnections?.length || 0,
          received: receivedConnections?.length || 0,
          matched: matchedConnections?.length || 0,
          views: views?.length || 0
        }}
        likeLimits={activeTab === 'received' ? likeLimits : undefined}
      />
      
      {/* Tab content appears naturally after navigation */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 pt-6 pb-24 md:pb-8">
            <ConnectionTabs 
              sentConnections={sentConnections || []}
              receivedConnections={receivedConnections || []}
              matches={matchedConnections || []}
              onProfileAction={handleProfileAction}
              activeTab={getTabForConnectionTabs(activeTab)}
              onTabChange={(tab) => handleTabChange(tab === "matches" ? "matched" : tab)}
              loading={loading}
              viewsCount={views?.length || 0}
              canLike={likeLimits.canLike}
              likeLimits={likeLimits}
              shouldShowViewsBlur={shouldShowViewsBlur}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Connections;
